import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './ManageRentals.css';
import PropertyDetails from './PropertyDetails';
import LocationPicker from './LocationPicker';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { FaStar } from 'react-icons/fa';
import {
  createProperty,
  createPropertyForLandlord,
  deleteProperty,
  getMyProperties,
  getPropertyById,
  getPropertyUnits,
  uploadPropertyImage,
  uploadAgreementDocument,
  getManageBnbBookings,
} from '../../../api/services/propertyService';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';
import { fetchAuthenticatedImage } from '../../../api/services/authService';
import { storeImageInCache, getImageFromCache, BROKEN_IMAGE_PLACEHOLDER_SRC } from '../../../utils/imageCache';
import { getMyLandlords } from '../../../api/services/agentService';
import { filterPropertiesByScope, setPropertyScope } from '../../../utils/propertyScope';
import { filterUnitsByListingScope } from '../../../utils/publicListingScope';
import { computePropertyUnitStats, parseUnitsFromApiResponse } from '../../../utils/propertyUnitStats';
import { hydrateUnitsWithActiveTenancy } from '../../../utils/propertyUnitTenancy';

/** History marker so mobile/browser Back pops property detail instead of leaving the app. */
const PROPERTY_DETAIL_HISTORY_KEY = 'tenantscore_propertyDetail';

const ManageRentals = ({
  agentId = null,
  isAgentView: propIsAgentView = false,
  propertyScope = 'landlord',
}) => {
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [properties, setProperties] = useState([]);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [propertyToEdit, setPropertyToEdit] = useState(null);
  const [deletingProperty, setDeletingProperty] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [authenticatedImages, setAuthenticatedImages] = useState({}); // { propertyId_imageIndex: blobUrl }
  const isAgentView = propIsAgentView || agentId !== null;
  const [landlords, setLandlords] = useState([]);
  const [selectedLandlordId, setSelectedLandlordId] = useState('');
  // BNB occupancy: { [propertyId]: { occupiedUnitIds: Set, totalUnits: number } }
  const [bnbOccupancyByProperty, setBnbOccupancyByProperty] = useState({});

  const [agentFilter, setAgentFilter] = useState('all'); // 'all', 'assigned', 'personal'

  // Filter properties for agent view and search
  const filteredProperties = useMemo(() => {
    const scopedProperties = filterPropertiesByScope(properties, propertyScope, {
      // For BnB, the backend endpoint already scopes results,
      // so include unscoped to avoid hiding empty-unit properties.
      includeUnscoped: true,
      inferFromUnits: true,
    });

    let filtered = scopedProperties;

    // Filter by agent view (assigned vs personal) if applicable
    if (isAgentView) {
      if (agentFilter === 'assigned') {
        // Properties with an assignment count or not owned by the current agent
        // On client-side, we can distinguish personal properties by checking if ownerId matches (or doesn't have assignedAgentName if it is agent's own)
        // However, a simple check is: if ownerId is the logged-in agent, it is personal.
        // Let's filter based on whether the property was loaded from personal vs assigned.
        // To make it robust, let's tag properties when mapping them.
        filtered = scopedProperties.filter(p => p.isAssignedProperty);
      } else if (agentFilter === 'personal') {
        filtered = scopedProperties.filter(p => p.isPersonalProperty);
      }
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(property => {
        const nameMatch = property.name?.toLowerCase().includes(query);
        const addressMatch = property.address?.toLowerCase().includes(query);
        const agentMatch = property.assignedAgentName?.toLowerCase().includes(query);
        return nameMatch || addressMatch || agentMatch;
      });
    }

    return filtered;
  }, [properties, isAgentView, agentFilter, searchQuery, propertyScope]);

  // Fetch properties from API on mount and when search query changes
  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        if (isAgentView) {
          const isBnbScope = String(propertyScope).toLowerCase() === 'bnb';

          // Fetch both assigned properties AND agent's own personal properties in parallel
          const [assignmentsRes, personalRes] = await Promise.allSettled([
            getMyAgentAssignments({ page: 0, size: 100 }),
            getMyProperties({
              page: 0,
              size: 100,
              search: searchQuery,
              dashboardScope: isBnbScope ? 'BNB' : undefined,
            }),
          ]);

          // Collect unique property IDs from assignments
          const assignmentList = assignmentsRes.status === 'fulfilled'
            ? (Array.isArray(assignmentsRes.value?.data?.content) ? assignmentsRes.value.data.content : [])
            : [];
          const assignedPropertyIds = [...new Set(assignmentList.map((a) => a?.propertyId).filter(Boolean))];

          // Collect personal property IDs (properties the agent owns themselves)
          const personalProperties = personalRes.status === 'fulfilled'
            ? (Array.isArray(personalRes.value?.data?.content) ? personalRes.value.data.content : [])
            : [];
          const personalPropertyIds = personalProperties.map((p) => p?.id).filter(Boolean);

          console.log(`[Agent ManageRentals] Loaded ${assignedPropertyIds.length} assigned property IDs and ${personalPropertyIds.length} personal property IDs.`);

          // Merge, deduplicating by ID (personal first so owner detail takes priority)
          const allPropertyIds = [...new Set([...personalPropertyIds, ...assignedPropertyIds])];

          // Fetch full details for each unique property
          const detailResponses = await Promise.all(
            allPropertyIds.map(async (propertyId) => {
              try {
                const res = await getPropertyById(propertyId, true, {
                  dashboardScope: isBnbScope ? 'BNB' : 'RENTAL',
                });
                let detail = res?.data || null;
                if (!detail) return null;
                try {
                  const unitsResponse = await getPropertyUnits(propertyId, {
                    dashboardScope: isBnbScope ? 'BNB' : 'RENTAL',
                  });
                  let units = parseUnitsFromApiResponse(unitsResponse);
                  units = filterUnitsByListingScope(units, isBnbScope ? 'BNB' : 'RENTAL');
                  if (!isBnbScope) {
                    units = await hydrateUnitsWithActiveTenancy(propertyId, units);
                  }
                  detail = { ...detail, units, unitCount: units.length };
                } catch (_) {
                  // Keep property payload units if units endpoint fails
                }
                return detail;
              } catch (_) {
                return null;
              }
            })
          );

          const detailsById = new Map();
          detailResponses.forEach((p) => {
            if (p?.id) detailsById.set(p.id, p);
          });

          const mapped = allPropertyIds.map((propertyId) => {
            const isPersonal = personalPropertyIds.includes(propertyId);
            const isAssigned = assignedPropertyIds.includes(propertyId);

            let detail = detailsById.get(propertyId);
            if (detail) {
              return {
                ...detail,
                isPersonalProperty: isPersonal,
                isAssignedProperty: isAssigned
              };
            }
            // Fallback: use assignment data or personal property data
            const assignment = assignmentList.find((a) => a?.propertyId === propertyId) || {};
            const personal = personalProperties.find((p) => p?.id === propertyId);
            if (personal) {
              return {
                ...personal,
                isPersonalProperty: isPersonal,
                isAssignedProperty: isAssigned
              };
            }
            return {
              id: propertyId,
              name: assignment?.propertyName || 'Property',
              address: assignment?.propertyAddress || '',
              location: { city: assignment?.propertyAddress || '' },
              unitCount: 0,
              units: [],
              imageUrls: [],
              imageUrl: '',
              isPersonalProperty: isPersonal,
              isAssignedProperty: isAssigned
            };
          });

          setProperties(mapped);
        } else {
          const response = await getMyProperties({
            page: 0,
            size: 100,
            search: searchQuery,
            dashboardScope: String(propertyScope).toLowerCase() === 'bnb' ? 'BNB' : undefined,
          });

          if (response.status === 'success' && response.data?.content) {
            const baseProperties = response.data.content;
            const isBnbScope = String(propertyScope).toLowerCase() === 'bnb';
            const enrichedProperties = await Promise.all(
              baseProperties.map(async (property) => {
                const propertyId = property?.id;
                if (!propertyId) return property;
                try {
                  // Always fetch details to unify shape
                  const detailResponse = await getPropertyById(propertyId, true, {
                    dashboardScope: isBnbScope ? 'BNB' : 'RENTAL',
                  });
                  let merged = detailResponse?.status === 'success' && detailResponse?.data
                    ? { ...property, ...detailResponse.data }
                    : property;

                  // Always load units from the units endpoint (property payload can be stale)
                  try {
                    const unitsResponse = await getPropertyUnits(propertyId, {
                      dashboardScope: isBnbScope ? 'BNB' : 'RENTAL',
                    });
                    let units = parseUnitsFromApiResponse(unitsResponse);
                    units = filterUnitsByListingScope(units, isBnbScope ? 'BNB' : 'RENTAL');
                    if (!isBnbScope) {
                      units = await hydrateUnitsWithActiveTenancy(propertyId, units);
                    }
                    merged = { ...merged, units, unitCount: units.length };
                  } catch (_) {
                    // Ignore unit fetch errors; keep merged as-is
                  }
                  return merged;
                } catch (_) {
                  // Keep base property if details call fails.
                }
                return property;
              })
            );
            setProperties(enrichedProperties);
          } else {
            setProperties([]);
          }
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        setErrorMessage(error.message || 'Failed to load properties. Please try again.');
        setProperties([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [searchQuery, isAgentView, propertyScope]);

  // For BNB scope: fetch bookings for each property and determine which units are occupied today
  useEffect(() => {
    if (String(propertyScope).toLowerCase() !== 'bnb' || properties.length === 0) {
      setBnbOccupancyByProperty({});
      return;
    }
    let cancelled = false;

    const fetchBnbOccupancy = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const ACTIVE_STATUSES = ['CONFIRMED', 'APPROVED', 'ASSIGNED', 'CHECKED_IN'];

      const results = await Promise.all(
        properties.map(async (property) => {
          const propId = property?.id;
          if (!propId) return null;
          try {
            // Fetch all bookings for this property (no status filter = all)
            const res = await getManageBnbBookings(propId);
            const bookings = Array.isArray(res?.data) ? res.data : [];

            // Find units that have an active booking covering today
            const occupiedUnitIds = new Set();
            bookings.forEach((b) => {
              const status = String(b?.status || '').toUpperCase();
              if (!ACTIVE_STATUSES.includes(status)) return;

              const checkIn = new Date(b.checkInDate);
              const checkOut = new Date(b.checkOutDate);
              checkIn.setHours(0, 0, 0, 0);
              checkOut.setHours(0, 0, 0, 0);

              if (isNaN(checkIn.getTime()) || isNaN(checkOut.getTime())) return;

              // Unit is occupied if today >= checkIn AND today < checkOut
              if (today >= checkIn && today < checkOut) {
                const unitId = b.unitId || b.unitID || b.unit?.id || b.propertyUnitId;
                if (unitId) occupiedUnitIds.add(String(unitId));
              }
            });

            return { propId, occupiedUnitIds };
          } catch (_) {
            return { propId, occupiedUnitIds: new Set() };
          }
        })
      );

      if (cancelled) return;

      const map = {};
      results.forEach((r) => {
        if (r) map[r.propId] = { occupiedUnitIds: r.occupiedUnitIds };
      });
      setBnbOccupancyByProperty(map);
    };

    fetchBnbOccupancy();
    return () => { cancelled = true; };
  }, [properties, propertyScope]);

  useEffect(() => {
    if (!isAgentView) return undefined;
    let cancelled = false;
    const fetchLandlords = async () => {
      try {
        const res = await getMyLandlords();
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setLandlords(list);
          if (!selectedLandlordId && list.length === 1 && list[0]?.landlordId) {
            setSelectedLandlordId(String(list[0].landlordId));
          }
        }
      } catch (error) {
        // Don't block the page; landlord selection is required only when creating a property.
        console.error('Error fetching agent landlords:', error);
      }
    };
    fetchLandlords();
    return () => {
      cancelled = true;
    };
  }, [isAgentView, selectedLandlordId]);

  useEffect(() => {
    // If user clicked "Add Property" from Overview, open the modal automatically.
    try {
      const shouldOpen = localStorage.getItem('landlord_open_add_property') === '1';
      if (shouldOpen) {
        setShowAddProperty(true);
        localStorage.removeItem('landlord_open_add_property');
      }
    } catch (_) {
      // ignore
    }
  }, []);

  // Fetch authenticated images for all properties
  // Temporary suppression: react-scripts eslint cache is reporting a missing
  // `allowAddProperty` dependency for this effect, but that identifier does not
  // exist in this component scope.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadAuthenticatedImages = async () => {
      const newAuthenticatedImages = {};

      // Process all properties
      for (const property of filteredProperties) {
        const propertyImages = property.imageUrls && property.imageUrls.length > 0
          ? property.imageUrls
          : (property.imageUrl ? [property.imageUrl] : []);

        const displayImages = propertyImages.slice(0, 1); // Only show first image

        // Fetch each image
        for (let imgIndex = 0; imgIndex < displayImages.length; imgIndex++) {
          const imageUrl = displayImages[imgIndex];
          const imageKey = `${property.id}_${imgIndex}`;

          // Skip if already a blob URL or data URL
          if (imageUrl && (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:'))) {
            newAuthenticatedImages[imageKey] = imageUrl;
            continue;
          }

          // Skip if no URL
          if (!imageUrl || !imageUrl.startsWith('http')) {
            continue;
          }

          try {
            // Check cache first
            const cachedImage = getImageFromCache(imageUrl);
            if (cachedImage) {
              // Use cached image
              newAuthenticatedImages[imageKey] = cachedImage;
            } else {
              // Fetch from server (blob URLs are cached in authService until logout — do not revoke here)
              const blobUrl = await fetchAuthenticatedImage(imageUrl);
              if (blobUrl) {
                newAuthenticatedImages[imageKey] = blobUrl;
                await storeImageInCache(imageUrl, blobUrl);
              }
            }
          } catch (error) {
            console.error(`Error loading image for property ${property.id}, image ${imgIndex}:`, error);
          }
        }
      }

      setAuthenticatedImages(newAuthenticatedImages);
    };

    if (filteredProperties.length > 0) {
      loadAuthenticatedImages();
    }
  }, [filteredProperties]);

  const [propertyForm, setPropertyForm] = useState({
    name: '',
    address: '',
    description: '',
    location: { lat: '', lng: '', city: '', area: '', locality: '', suburb: '' },
    amenities: [],
    imageFiles: [],
    imagePreviews: [],
    contacts: {
      phone: '',
      email: '',
      caretakerPhone: ''
    },
    paymentDetails: {
      method: 'PAYBILL',
      paybillNumber: '',
      paybillAccount: '',
      accountName: '',
      acceptsPochi: false,
      acceptsSendMoney: false
    },
    agreementFile: null,
    agreementPreview: null,
    unitCount: '',
    published: true,
    rentGraceDays: '',
    lateFeeType: 'FIXED_PER_DAY',
    lateFeeValue: '',
    waterIncludedInRent: false,
    waterRatePerUnit: ''
  });

  const availableAmenities = [
    'parking', 'balcony', 'gym', 'swimming_pool', 'security',
    'elevator', 'wifi', 'water', 'electricity', 'garden',
    'playground', 'cctv', 'generator', 'water_heater', 'air_conditioning'
  ];

  const paymentMethods = [
    { value: 'PAYBILL', label: 'Paybill' },
    { value: 'TILL', label: 'Till Number' },
    { value: 'POCHI', label: 'Pochi' },
    { value: 'SEND_MONEY', label: 'Send Money' },
    { value: 'BANK', label: 'Bank Transfer' },
    { value: 'CASH', label: 'Cash' },
    { value: 'MULTIPLE', label: 'Multiple Methods' }
  ];

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage('');

    try {
      const isPersonalProperty = selectedLandlordId === '__personal__';
      if (isAgentView && !String(selectedLandlordId || '').trim()) {
        throw new Error('Please select a landlord or choose "Personal Property"');
      }

      // Validate required fields
      if (!propertyForm.name || !propertyForm.address) {
        throw new Error('Property name and address are required');
      }

      if (propertyForm.imageFiles.length === 0) {
        throw new Error('At least one property image is required');
      }

      if (propertyForm.imageFiles.length > 6) {
        throw new Error('Maximum 6 images allowed');
      }

      if (!propertyForm.unitCount || parseInt(propertyForm.unitCount) < 1) {
        throw new Error('Unit count must be at least 1');
      }

      // Upload images first
      const imageUrls = [];
      for (let i = 0; i < propertyForm.imageFiles.length; i++) {
        const file = propertyForm.imageFiles[i];
        const previewUrl = propertyForm.imagePreviews[i];

        const uploadResponse = await uploadPropertyImage(file);
        if (uploadResponse.status === 'success' && uploadResponse.data?.url) {
          const imageUrl = uploadResponse.data.url;
          imageUrls.push(imageUrl);

          // Store uploaded image in cache using the preview URL (blob or data URL)
          if (previewUrl) {
            await storeImageInCache(imageUrl, previewUrl);
          }
        } else {
          throw new Error('Failed to upload one or more images');
        }
      }

      // Upload agreement document if provided
      let agreementUrl = null;
      if (propertyForm.agreementFile) {
        const agreementResponse = await uploadAgreementDocument(propertyForm.agreementFile);
        if (agreementResponse.status === 'success' && agreementResponse.data?.url) {
          agreementUrl = agreementResponse.data.url;
        } else {
          throw new Error('Failed to upload agreement document');
        }
      }

      // Prepare payment details based on method
      let paymentDetails = {};
      const method = propertyForm.paymentDetails.method;
      const acceptsPochi = !!propertyForm.paymentDetails.acceptsPochi;
      const acceptsSendMoney = !!propertyForm.paymentDetails.acceptsSendMoney;

      if (method === 'PAYBILL') {
        paymentDetails = {
          method: 'PAYBILL',
          paybillNumber: propertyForm.paymentDetails.paybillNumber,
          paybillAccount: propertyForm.paymentDetails.paybillAccount,
          accountName: propertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'TILL') {
        paymentDetails = {
          method: 'TILL',
          tillNumber: propertyForm.paymentDetails.tillNumber,
          accountName: propertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'POCHI') {
        paymentDetails = {
          method: 'POCHI',
          pochiNumber: propertyForm.paymentDetails.pochiNumber,
          accountName: propertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'SEND_MONEY') {
        paymentDetails = {
          method: 'SEND_MONEY',
          phoneNumber: propertyForm.paymentDetails.phoneNumber,
          accountName: propertyForm.paymentDetails.accountName,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'BANK') {
        paymentDetails = {
          method: 'BANK',
          bankName: propertyForm.paymentDetails.bankName,
          accountName: propertyForm.paymentDetails.accountName,
          accountNumber: propertyForm.paymentDetails.accountNumber,
          branch: propertyForm.paymentDetails.branch,
          swiftCode: propertyForm.paymentDetails.swiftCode,
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'CASH') {
        paymentDetails = {
          method: 'CASH',
          instructions: propertyForm.paymentDetails.instructions || 'Pay at caretaker office',
          acceptsPochi,
          acceptsSendMoney
        };
      } else if (method === 'MULTIPLE') {
        paymentDetails = {
          method: 'MULTIPLE',
          options: propertyForm.paymentDetails.options || [],
          acceptsPochi,
          acceptsSendMoney
        };
      }

      // Prepare property data
      const propertyData = {
        name: propertyForm.name,
        address: propertyForm.address,
        location: {
          lat: parseFloat(propertyForm.location.lat) || 0,
          lng: parseFloat(propertyForm.location.lng) || 0,
          city: propertyForm.location.city || '',
          area: propertyForm.location.area || '',
          locality: propertyForm.location.locality || '',
          suburb: propertyForm.location.suburb || ''
        },
        description: propertyForm.description || '',
        amenities: propertyForm.amenities,
        imageUrls: imageUrls,
        contacts: {
          phone: propertyForm.contacts.phone || '',
          email: propertyForm.contacts.email || '',
          caretakerPhone: propertyForm.contacts.caretakerPhone || ''
        },
        paymentDetails: paymentDetails,
        agreementUrl: agreementUrl || undefined,
        unitCount: parseInt(propertyForm.unitCount),
        published: !!propertyForm.published
      };

      if (!propertyData.agreementUrl) delete propertyData.agreementUrl;

      const rentGraceDays = propertyForm.rentGraceDays === '' ? null : parseInt(propertyForm.rentGraceDays);
      const lateFeeValue = propertyForm.lateFeeValue === '' ? null : parseFloat(propertyForm.lateFeeValue);
      if (Number.isFinite(rentGraceDays) && rentGraceDays !== null) propertyData.rentGraceDays = rentGraceDays;
      if (propertyForm.lateFeeType && Number.isFinite(lateFeeValue) && lateFeeValue !== null) {
        propertyData.lateFeeType = propertyForm.lateFeeType;
        propertyData.lateFeeValue = lateFeeValue;
      }

      propertyData.waterIncludedInRent = !!propertyForm.waterIncludedInRent;
      if (!propertyForm.waterIncludedInRent) {
        const waterRate = propertyForm.waterRatePerUnit === '' ? null : parseFloat(propertyForm.waterRatePerUnit);
        if (Number.isFinite(waterRate)) propertyData.waterRatePerUnit = waterRate;
      }

      // Create property — personal means agent owns it (no landlordId param)
      const response = (isAgentView && !isPersonalProperty)
        ? await createPropertyForLandlord(selectedLandlordId, propertyData)
        : await createProperty(propertyData);

      if (response.status === 'success') {
        const createdPropertyId = response?.data?.id ?? response?.data?.propertyId;
        if (createdPropertyId != null && createdPropertyId !== '') {
          setPropertyScope(createdPropertyId, propertyScope);
        }
        // Refresh properties list for landlord view OR agent personal properties
        if (!isAgentView || isPersonalProperty) {
          const refreshResponse = await getMyProperties({
            page: 0,
            size: 100,
            search: '',
            dashboardScope: String(propertyScope).toLowerCase() === 'bnb' ? 'BNB' : undefined,
          });
          if (refreshResponse.status === 'success' && refreshResponse.data?.content) {
            setProperties(refreshResponse.data.content);
          }
        }

        // Reset form and close modal
        setPropertyForm({
          name: '',
          address: '',
          description: '',
          location: { lat: '', lng: '', city: '', area: '', locality: '', suburb: '' },
          amenities: [],
          imageFiles: [],
          imagePreviews: [],
          contacts: {
            phone: '',
            email: '',
            caretakerPhone: ''
          },
          paymentDetails: {
            method: 'PAYBILL',
            paybillNumber: '',
            paybillAccount: '',
            accountName: '',
            acceptsPochi: false,
            acceptsSendMoney: false
          },
          agreementFile: null,
          agreementPreview: null,
          unitCount: '',
          published: true,
          rentGraceDays: '',
          lateFeeType: 'FIXED_PER_DAY',
          lateFeeValue: '',
          waterIncludedInRent: false,
          waterRatePerUnit: ''
        });
        setShowAddProperty(false);
      } else {
        throw new Error(response.message || 'Failed to create property');
      }
    } catch (error) {
      console.error('Error creating property:', error);
      setErrorMessage(error.message || 'Failed to create property. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePropertyImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Limit to 6 images
    const filesToAdd = files.slice(0, 6 - propertyForm.imageFiles.length);
    if (filesToAdd.length === 0) {
      setErrorMessage('Maximum 6 images allowed');
      return;
    }

    const previews = filesToAdd.map(file => URL.createObjectURL(file));

    setPropertyForm((prev) => ({
      ...prev,
      imageFiles: [...prev.imageFiles, ...filesToAdd],
      imagePreviews: [...prev.imagePreviews, ...previews]
    }));
  };

  const handleRemoveImage = (index) => {
    setPropertyForm((prev) => {
      const newFiles = [...prev.imageFiles];
      const newPreviews = [...prev.imagePreviews];

      // Revoke object URL to free memory
      URL.revokeObjectURL(newPreviews[index]);

      newFiles.splice(index, 1);
      newPreviews.splice(index, 1);

      return {
        ...prev,
        imageFiles: newFiles,
        imagePreviews: newPreviews
      };
    });
  };

  const handleAgreementChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setPropertyForm((prev) => ({
      ...prev,
      agreementFile: file,
      agreementPreview: previewUrl
    }));
  };

  const toggleAmenity = (amenity) => {
    setPropertyForm((prev) => {
      const amenities = prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity];
      return { ...prev, amenities };
    });
  };

  const handleViewDetails = (propertyId) => {
    window.history.pushState(
      { [PROPERTY_DETAIL_HISTORY_KEY]: true, propertyId },
      '',
      window.location.href
    );
    setSelectedPropertyId(propertyId);
  };

  useEffect(() => {
    if (!selectedPropertyId) return undefined;
    const onPopState = () => {
      setSelectedPropertyId(null);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [selectedPropertyId]);

  const handleEditClick = (propertyId, e) => {
    e?.stopPropagation?.();
    setPropertyToEdit(propertyId);
  };

  const handleEditPropertyClose = () => {
    setPropertyToEdit(null);
  };

  const handlePropertyUpdated = async (updatedPropertyId) => {
    const isBnbScope = String(propertyScope).toLowerCase() === 'bnb';
    try {
      const res = await getPropertyById(updatedPropertyId, true, {
        dashboardScope: isBnbScope ? 'BNB' : 'RENTAL',
      });
      const detail = res?.data;
      if (!detail) return;

      let units = detail.units || [];
      try {
        const unitsResponse = await getPropertyUnits(updatedPropertyId, {
          dashboardScope: isBnbScope ? 'BNB' : 'RENTAL',
        });
        units = parseUnitsFromApiResponse(unitsResponse);
        units = filterUnitsByListingScope(units, isBnbScope ? 'BNB' : 'RENTAL');
        if (!isBnbScope) {
          units = await hydrateUnitsWithActiveTenancy(updatedPropertyId, units);
        }
      } catch (_) {
        // Keep property payload units if units endpoint fails
      }

      const refreshed = { ...detail, units, unitCount: units.length };
      setProperties((prev) => prev.map((p) => (p.id === updatedPropertyId ? { ...p, ...refreshed } : p)));
    } catch (_) {
      // List stays as-is if refresh fails
    }
  };

  const handleDeleteClick = (propertyId, e) => {
    e.stopPropagation();
    setDeleteError('');
    setPropertyToDelete(propertyId);
  };

  const handleConfirmDelete = async () => {
    if (!propertyToDelete || deletingProperty) return;

    setDeletingProperty(true);
    setDeleteError('');
    try {
      await deleteProperty(propertyToDelete);
      setProperties((prev) => prev.filter((p) => p.id !== propertyToDelete));
      setPropertyToDelete(null);
    } catch (error) {
      setDeleteError(error.message || 'Failed to delete property. Please try again.');
    } finally {
      setDeletingProperty(false);
    }
  };

  const handleCancelDelete = () => {
    if (deletingProperty) return;
    setDeleteError('');
    setPropertyToDelete(null);
  };

  // If a property is selected, show PropertyDetails
  if (selectedPropertyId) {
    return (
      <PropertyDetails
        propertyId={selectedPropertyId}
        propertyScope={propertyScope}
        onBack={() => {
          // If the user came directly to a URL with history pushed, back will pop it.
          // Otherwise, to be safe, also explicitly unset the ID
          window.history.back();
          setTimeout(() => {
            setSelectedPropertyId(null);
          }, 50);
        }}
      />
    );
  }

  return (
    <div className="manage-rentals-container">
      <CollapsibleTopPanel id="manage-rentals-top-panel" defaultOpenOnMobile={false}>
        <div className="page-header">
          <h1 className="page-title">Manage Rentals</h1>
          <div className="page-header-actions">
            {isAgentView && (
              <div className="agent-filter-select-container" style={{ marginRight: '10px' }}>
                <select
                  value={agentFilter}
                  onChange={(e) => setAgentFilter(e.target.value)}
                  className="search-input"
                  style={{ minWidth: '180px', height: '36px', padding: '0 10px', borderRadius: '4px', border: '1px solid #ccc' }}
                >
                  <option value="all">🏢 All Properties</option>
                  <option value="assigned">👥 Assigned Properties</option>
                  <option value="personal">🏠 Personal Properties</option>
                </select>
              </div>
            )}
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="btn btn-primary btn-sm btn-add-agent" onClick={() => setShowAddProperty(true)}>
              Add Property
            </button>
          </div>
        </div>
      </CollapsibleTopPanel>

      {errorMessage && (
        <div className="error-message" style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee',
          color: '#c33',
          borderRadius: '4px'
        }}>
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="empty-state">Loading properties...</div>
      ) : filteredProperties.length === 0 ? (
        <div className="empty-state">
          {isAgentView
            ? (agentFilter === 'assigned'
                ? "No assigned properties found."
                : (agentFilter === 'personal'
                    ? "No personal properties found."
                    : "No properties found."))
            : "No properties yet. Click \"Add Property\" to create your first property."}
        </div>
      ) : (
        <div className="properties-grid-overview">
          {filteredProperties.map((property) => {
            const bnbOccupiedSet = bnbOccupancyByProperty[property?.id]?.occupiedUnitIds;
            const { totalUnits, occupiedCount, vacantCount, damagedCount } = computePropertyUnitStats(
              property,
              {
                propertyScope,
                bnbOccupiedUnitIds: bnbOccupiedSet instanceof Set ? bnbOccupiedSet : null,
              }
            );

            // Get images - prefer imageUrls array, fallback to imageUrl
            const propertyImages = property.imageUrls && property.imageUrls.length > 0
              ? property.imageUrls
              : (property.imageUrl ? [property.imageUrl] : []);
            const hasMoreImages = propertyImages.length > 1;
            const displayImages = propertyImages.slice(0, 1); // Only show first image

            const addrLine = property.address?.trim() || '';
            const cityLine = property.location?.city?.trim() || '';
            const primaryLocationLine = addrLine || cityLine;
            const showCitySubline =
              !!addrLine &&
              !!cityLine &&
              addrLine.toLowerCase() !== cityLine.toLowerCase();

            return (
              <div key={property.id} className="property-overview-card">
                <button
                  type="button"
                  className="property-overview-card__delete"
                  onClick={(e) => handleDeleteClick(property.id, e)}
                  aria-label={`Delete ${property.name}`}
                  title="Delete property"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6M14 11v6" />
                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                  </svg>
                </button>
                {/* Property Images */}
                {displayImages.length > 0 && (
                  <div className="property-images-container">
                    <div className="property-images-grid single-image">
                      {displayImages.map((imageUrl, imgIndex) => {
                        const imageKey = `${property.id}_${imgIndex}`;
                        const authenticatedUrl = authenticatedImages[imageKey] || imageUrl;

                        return (
                          <div
                            key={imgIndex}
                            className="property-image-wrapper main-image"
                          >
                            <img
                              src={authenticatedUrl}
                              alt={`${property.name}`}
                              className="property-image"
                              onError={(e) => {
                                const el = e.currentTarget;
                                el.onerror = null;
                                el.src = BROKEN_IMAGE_PLACEHOLDER_SRC;
                              }}
                            />
                            {hasMoreImages && (
                              <div className="image-count-badge">
                                +{propertyImages.length - 1}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="property-overview-header">
                  <h3>{property.name}</h3>
                  <div className="property-rating-overview">
                    <div className="property-stars">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <FaStar key={s} className={`tiny-star ${s <= (property.avgRating || 0) ? 'active' : 'empty'}`} />
                      ))}
                    </div>
                    {property.ratingCount > 0 && (
                      <span className="property-rating-count">
                        {(property.avgRating || 0).toFixed(1)} ({property.ratingCount})
                      </span>
                    )}
                  </div>
                  {primaryLocationLine && (
                    <p className="property-overview-address">{primaryLocationLine}</p>
                  )}
                  {showCitySubline && (
                    <p className="property-city" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {cityLine}
                    </p>
                  )}
                </div>
                <div className="property-overview-stats">
                  <div className="property-stat-item">
                    <span className="stat-label">Total Units</span>
                    <span className="stat-value">{totalUnits}</span>
                  </div>
                  <div className="property-stat-item success">
                    <span className="stat-label">Occupied</span>
                    <span className="stat-value">{occupiedCount}</span>
                  </div>
                  <div className="property-stat-item warning">
                    <span className="stat-label">Vacant</span>
                    <span className="stat-value">{vacantCount}</span>
                  </div>
                  {damagedCount > 0 && (
                    <div className="property-stat-item error">
                      <span className="stat-label">Damaged</span>
                      <span className="stat-value">{damagedCount}</span>
                    </div>
                  )}
                </div>
                <div className="property-action-buttons">
                  <button
                    type="button"
                    className="btn-view-property"
                    onClick={() => handleViewDetails(property.id)}
                  >
                    View Details
                  </button>
                  <button
                    type="button"
                    className="btn-edit-property"
                    onClick={(e) => handleEditClick(property.id, e)}
                  >
                    Edit Property
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {propertyToEdit && (
        <PropertyDetails
          propertyId={propertyToEdit}
          propertyScope={propertyScope}
          editModalOnly
          onEditPropertyClose={handleEditPropertyClose}
          onPropertyUpdated={handlePropertyUpdated}
        />
      )}

      {/* Delete Confirmation Modal */}
      {propertyToDelete && createPortal(
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Property</h2>
              <button className="close-btn" onClick={handleCancelDelete} disabled={deletingProperty}>×</button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this property? This action cannot be undone.</p>
              <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
                All units and associated data will be permanently removed.
              </p>
              {deleteError && (
                <p style={{ fontSize: '14px', color: '#c33', marginTop: '12px' }}>
                  {deleteError}
                </p>
              )}
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancelDelete} disabled={deletingProperty}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleConfirmDelete} disabled={deletingProperty}>
                {deletingProperty ? 'Deleting...' : 'Delete Property'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add Property Modal */}
      {showAddProperty && createPortal(
        <div className="modal-overlay" onClick={() => !submitting && setShowAddProperty(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Create New Property</h2>
              <button className="close-btn" onClick={() => !submitting && setShowAddProperty(false)} disabled={submitting}>×</button>
            </div>
            {errorMessage && (
              <div style={{ padding: '12px', margin: '16px', backgroundColor: '#fee', color: '#c33', borderRadius: '4px' }}>
                {errorMessage}
              </div>
            )}
            <form onSubmit={handlePropertySubmit} className="modal-form">
              {isAgentView && (
                <div className="form-group">
                  <label>Property Ownership *</label>
                  <select
                    value={selectedLandlordId}
                    onChange={(e) => setSelectedLandlordId(e.target.value)}
                    disabled={submitting}
                  >
                    <option value="">Select ownership…</option>
                    <option value="__personal__">🏠 Personal Property (Mine)</option>
                    {landlords.length > 0 && (
                      <optgroup label="Create for a Landlord">
                        {landlords.map((l) => (
                          <option key={l?.landlordId || l?.landlordEmail} value={String(l?.landlordId || '')}>
                            {l?.landlordName || l?.landlordEmail || l?.landlordId || 'Landlord'}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedLandlordId === '__personal__' && (
                    <small className="form-hint" style={{ color: '#2a7', marginTop: '4px', display: 'block' }}>
                      This property will be created under your own agent account.
                    </small>
                  )}
                  {selectedLandlordId && selectedLandlordId !== '__personal__' && (
                    <small className="form-hint" style={{ marginTop: '4px', display: 'block' }}>
                      This property will be created on behalf of the selected landlord.
                    </small>
                  )}
                </div>
              )}
              <div className="form-group">
                <label>Property Name *</label>
                <input
                  type="text"
                  value={propertyForm.name}
                  onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })}
                  required
                  placeholder="Enter property name"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Address *</label>
                <input
                  type="text"
                  value={propertyForm.address}
                  onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
                  required
                  placeholder="Enter property address"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Location</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                  <div>
                    <input
                      type="number"
                      step="any"
                      value={propertyForm.location.lat}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        location: { ...propertyForm.location, lat: e.target.value }
                      })}
                      placeholder="Latitude"
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      step="any"
                      value={propertyForm.location.lng}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        location: { ...propertyForm.location, lng: e.target.value }
                      })}
                      placeholder="Longitude"
                      disabled={submitting}
                    />
                  </div>
                </div>
                <input
                  type="text"
                  value={propertyForm.location.city}
                  onChange={(e) => setPropertyForm({
                    ...propertyForm,
                    location: { ...propertyForm.location, city: e.target.value }
                  })}
                  placeholder="City"
                  style={{ marginBottom: '8px' }}
                  disabled={submitting}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={propertyForm.location.area}
                    onChange={(e) => setPropertyForm({
                      ...propertyForm,
                      location: { ...propertyForm.location, area: e.target.value }
                    })}
                    placeholder="Area (e.g. Westlands)"
                    disabled={submitting}
                  />
                  <input
                    type="text"
                    value={propertyForm.location.suburb}
                    onChange={(e) => setPropertyForm({
                      ...propertyForm,
                      location: { ...propertyForm.location, suburb: e.target.value }
                    })}
                    placeholder="Suburb (optional)"
                    disabled={submitting}
                  />
                </div>
                <input
                  type="text"
                  value={propertyForm.location.locality}
                  onChange={(e) => setPropertyForm({
                    ...propertyForm,
                    location: { ...propertyForm.location, locality: e.target.value }
                  })}
                  placeholder="Locality (optional)"
                  style={{ marginBottom: '8px' }}
                  disabled={submitting}
                />
                <LocationPicker
                  location={propertyForm.location}
                  onLocationChange={(newLocation) => {
                    setPropertyForm((prev) => ({
                      ...prev,
                      location: { ...prev.location, ...newLocation }
                    }));
                  }}
                  height="350px"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={propertyForm.description}
                  onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                  rows="4"
                  placeholder="Enter property description"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Amenities</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {availableAmenities.map((amenity) => (
                    <label key={amenity} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={propertyForm.amenities.includes(amenity)}
                        onChange={() => toggleAmenity(amenity)}
                        disabled={submitting}
                        style={{ marginRight: '6px' }}
                      />
                      <span style={{ textTransform: 'capitalize' }}>{amenity.replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Property Images * (1-6 images)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePropertyImagesChange}
                  disabled={submitting || propertyForm.imageFiles.length >= 6}
                />
                <small className="form-hint">
                  {propertyForm.imageFiles.length}/6 images selected
                </small>
                {propertyForm.imagePreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
                    {propertyForm.imagePreviews.map((preview, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #e0e0e0'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          disabled={submitting}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: 'rgba(0,0,0,0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '16px',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Unit Count *</label>
                <input
                  type="number"
                  min="1"
                  value={propertyForm.unitCount}
                  onChange={(e) => setPropertyForm({ ...propertyForm, unitCount: e.target.value })}
                  required
                  placeholder="Enter number of units"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Contacts</label>
                <input
                  type="tel"
                  value={propertyForm.contacts.phone}
                  onChange={(e) => setPropertyForm({
                    ...propertyForm,
                    contacts: { ...propertyForm.contacts, phone: e.target.value }
                  })}
                  placeholder="Phone number"
                  disabled={submitting}
                />
                <input
                  type="email"
                  value={propertyForm.contacts.email}
                  onChange={(e) => setPropertyForm({
                    ...propertyForm,
                    contacts: { ...propertyForm.contacts, email: e.target.value }
                  })}
                  placeholder="Email"
                  style={{ marginTop: '8px' }}
                  disabled={submitting}
                />
                <input
                  type="tel"
                  value={propertyForm.contacts.caretakerPhone}
                  onChange={(e) => setPropertyForm({
                    ...propertyForm,
                    contacts: { ...propertyForm.contacts, caretakerPhone: e.target.value }
                  })}
                  placeholder="Caretaker phone"
                  style={{ marginTop: '8px' }}
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={propertyForm.paymentDetails.method}
                  onChange={(e) => setPropertyForm({
                    ...propertyForm,
                    paymentDetails: { ...propertyForm.paymentDetails, method: e.target.value }
                  })}
                  disabled={submitting}
                >
                  {paymentMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!propertyForm.paymentDetails.acceptsPochi}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, acceptsPochi: e.target.checked }
                      })}
                      disabled={submitting}
                      style={{ marginRight: '6px' }}
                    />
                    <span>Accepts Pochi</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!propertyForm.paymentDetails.acceptsSendMoney}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, acceptsSendMoney: e.target.checked }
                      })}
                      disabled={submitting}
                      style={{ marginRight: '6px' }}
                    />
                    <span>Accepts Send Money</span>
                  </label>
                </div>

                {propertyForm.paymentDetails.method === 'PAYBILL' && (
                  <>
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.paybillNumber}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, paybillNumber: e.target.value }
                      })}
                      placeholder="Paybill Number"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.paybillAccount}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, paybillAccount: e.target.value }
                      })}
                      placeholder="Paybill Account"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.accountName}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                  </>
                )}

                {propertyForm.paymentDetails.method === 'TILL' && (
                  <>
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.tillNumber || ''}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, tillNumber: e.target.value }
                      })}
                      placeholder="Till Number"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.accountName}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                  </>
                )}

                {propertyForm.paymentDetails.method === 'POCHI' && (
                  <>
                    <input
                      type="tel"
                      value={propertyForm.paymentDetails.pochiNumber || ''}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, pochiNumber: e.target.value }
                      })}
                      placeholder="Pochi Number"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.accountName}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                  </>
                )}

                {propertyForm.paymentDetails.method === 'SEND_MONEY' && (
                  <>
                    <input
                      type="tel"
                      value={propertyForm.paymentDetails.phoneNumber || ''}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, phoneNumber: e.target.value }
                      })}
                      placeholder="Phone Number"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.accountName}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                  </>
                )}

                {propertyForm.paymentDetails.method === 'BANK' && (
                  <>
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.bankName || ''}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, bankName: e.target.value }
                      })}
                      placeholder="Bank Name"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.accountName}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, accountName: e.target.value }
                      })}
                      placeholder="Account Name"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.accountNumber || ''}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, accountNumber: e.target.value }
                      })}
                      placeholder="Account Number"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.branch || ''}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, branch: e.target.value }
                      })}
                      placeholder="Branch"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                    <input
                      type="text"
                      value={propertyForm.paymentDetails.swiftCode || ''}
                      onChange={(e) => setPropertyForm({
                        ...propertyForm,
                        paymentDetails: { ...propertyForm.paymentDetails, swiftCode: e.target.value }
                      })}
                      placeholder="SWIFT Code"
                      style={{ marginTop: '8px' }}
                      disabled={submitting}
                    />
                  </>
                )}

                {propertyForm.paymentDetails.method === 'CASH' && (
                  <textarea
                    value={propertyForm.paymentDetails.instructions || ''}
                    onChange={(e) => setPropertyForm({
                      ...propertyForm,
                      paymentDetails: { ...propertyForm.paymentDetails, instructions: e.target.value }
                    })}
                    placeholder="Payment instructions (e.g., Pay at caretaker office 8am–5pm)"
                    rows="3"
                    style={{ marginTop: '8px' }}
                    disabled={submitting}
                  />
                )}
              </div>

              <div className="form-group">
                <label>Agreement Document (Optional)</label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleAgreementChange}
                  disabled={submitting}
                />
                {propertyForm.agreementPreview && (
                  <div style={{ marginTop: '12px' }}>
                    <a
                      href={propertyForm.agreementPreview}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#0066cc', textDecoration: 'underline' }}
                    >
                      View Agreement Preview
                    </a>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Visibility</label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!propertyForm.published}
                    onChange={(e) => setPropertyForm({ ...propertyForm, published: e.target.checked })}
                    disabled={submitting}
                    style={{ marginRight: '6px' }}
                  />
                  <span>Published (visible on public listing)</span>
                </label>
              </div>

              <div className="form-group">
                <label>Water billing</label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={!!propertyForm.waterIncludedInRent}
                    onChange={(e) =>
                      setPropertyForm({ ...propertyForm, waterIncludedInRent: e.target.checked })
                    }
                    disabled={submitting}
                    style={{ marginRight: '6px' }}
                  />
                  <span>Water included in rent</span>
                </label>
                {!propertyForm.waterIncludedInRent && (
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={propertyForm.waterRatePerUnit}
                    onChange={(e) => setPropertyForm({ ...propertyForm, waterRatePerUnit: e.target.value })}
                    placeholder="Water rate per unit (e.g. 200)"
                    disabled={submitting}
                  />
                )}
              </div>

              <div className="form-group">
                <label>Late Fee Settings (Optional)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <input
                      type="number"
                      min="0"
                      value={propertyForm.rentGraceDays}
                      onChange={(e) => setPropertyForm({ ...propertyForm, rentGraceDays: e.target.value })}
                      placeholder="Rent grace days (e.g. 3)"
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <select
                      value={propertyForm.lateFeeType}
                      onChange={(e) => setPropertyForm({ ...propertyForm, lateFeeType: e.target.value })}
                      disabled={submitting}
                    >
                      <option value="FIXED_PER_DAY">Fixed per day</option>
                      <option value="PERCENT_OF_RENT_PER_DAY">Percent of rent per day</option>
                    </select>
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={propertyForm.lateFeeValue}
                  onChange={(e) => setPropertyForm({ ...propertyForm, lateFeeValue: e.target.value })}
                  placeholder="Late fee value (e.g. 200)"
                  style={{ marginTop: '8px' }}
                  disabled={submitting}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddProperty(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating...' : 'Create Property'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default ManageRentals;
